// Live weather on the board during the trip (F8): the current stage's pin goes to the worker,
// which asks MET Norway; the answer is kept on the phone (works offline for a while) and used
// for 3 hours, then the pack's typical climate takes over again. Not before or after the trip,
// and never for a wrapped stage (its weather would give it away).
import { useEffect } from 'react'
import { create } from 'zustand'
import type { Pack } from '../../shared/pack/schema'
import { LIVE_WEATHER_TTL_MS, type LiveWeather } from '../../shared/weather'
import type { TripClock } from '../domain/tripClock'
import { MYSTERY_SPOT } from '../domain/trip/visible'
import { api } from './api'
import { kvGet, kvSet } from './db'

const REFRESH_MS = 30 * 60_000

interface Stored {
  key: string
  weather: LiveWeather
  fetchedAt: number
}

const useLive = create<{ stored: Stored | null }>(() => ({ stored: null }))
let inflight: string | null = null

async function refresh(key: string, at: readonly [number, number]) {
  if (inflight === key) return
  inflight = key
  try {
    const cached = useLive.getState().stored ?? (await kvGet<Stored>('liveWeather')) ?? null
    if (cached) useLive.setState({ stored: cached })
    if (cached?.key === key && Date.now() - cached.fetchedAt < REFRESH_MS) return
    const weather = await api<LiveWeather>(`/api/weather?lat=${at[0]}&lng=${at[1]}`)
    const stored = { key, weather, fetchedAt: Date.now() }
    await kvSet('liveWeather', stored)
    useLive.setState({ stored })
  } catch {
    // Offline or no answer: the cached reading (if fresh) or the climate stands.
  } finally {
    inflight = null
  }
}

/** The stage whose weather the board shows now, if live weather applies. */
function liveStage(pack: Pick<Pack, 'itinerary'>, clock: TripClock) {
  // e2e runs with a fixed clock and must not depend on today's weather (or the network).
  if (import.meta.env.MODE === 'e2e') return null
  if (clock.phase !== 'during' || clock.stage.spot === MYSTERY_SPOT) return null
  return pack.itinerary.stages.find((s) => s.id === clock.stage.id) ?? null
}

export function useLiveWeather(pack: Pick<Pack, 'itinerary'>, clock: TripClock, nowMs: number): LiveWeather | null {
  const stage = liveStage(pack, clock)
  const key = stage ? `${stage.anchor[0]},${stage.anchor[1]}` : null
  const stored = useLive((s) => s.stored)
  useEffect(() => {
    if (!stage || !key) return
    void refresh(key, stage.anchor)
    const id = setInterval(() => void refresh(key, stage.anchor), REFRESH_MS)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the pin
  }, [key])
  if (!key || stored?.key !== key || nowMs - stored.weather.at > LIVE_WEATHER_TTL_MS) return null
  return stored.weather
}
